import { ActiveFilters, CountyRef, FilterOption, FilterOptions, ProjectResourcesRequest, SpecialtyGroupRef } from "@app/network-portfolio-management/interfaces";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";

export const ALL_OPTIONS_TEXT = "All";
export const COUNTY_KEY_SEPARATOR = "::";

export function toDropdownItems(options: FilterOption[] | undefined): DropdownItem<string>[] {
  if (!options) return [];
  return options.map((o) => ({ key: o.value, name: o.displayName }));
}

/**
 * Encode a county selection as a single string key. The nested dropdown only tracks child keys, so
 * the parent (state) is folded into each child key to round-trip {state, county}.
 */
export function countyKey(state: string, county: string): string {
  return `${state}${COUNTY_KEY_SEPARATOR}${county}`;
}

export function parseCountyKey(key: string): CountyRef | null {
  const sep = key.indexOf(COUNTY_KEY_SEPARATOR);
  if (sep < 0) return null;
  const state = key.substring(0, sep);
  const county = key.substring(sep + COUNTY_KEY_SEPARATOR.length);
  if (!state || !county) return null;
  return { county, state };
}

/**
 * Client-side identity for a specialty group, used as the dropdown key and as a Map key.
 *
 * A specialty group is identified by (code, name) together, so the identity has to encode both. The code is
 * length-prefixed, which makes this injective for any content: the prefix says exactly where the code ends, so
 * no two distinct groups can collide.
 *
 * This never leaves the browser: it is not persisted and not sent to the API. Nothing parses it back apart
 * either - resolve a key to its group with {@link specialtyGroupsByItemKey}.
 */
export function specialtyGroupItemKey(group: SpecialtyGroupRef): string {
  return `${group.code.length}${SPECIALTY_OPTION_VALUE_SEPARATOR}${group.code}${group.name}`;
}

// Must match the C# SpecialtyGroupFilter.OptionValueSeparator.
const SPECIALTY_OPTION_VALUE_SEPARATOR = "::";

/**
 * Decodes a specialty option's `value` from the API back to its (code, name) pair. The server encodes it as
 * `{code length}::{code}{name}` (SpecialtyGroupFilter.ToOptionValue); the length prefix is what makes the token
 * decodable even when the code itself contains the separator. Returns null for anything not in that shape.
 */
export function parseSpecialtyOptionValue(value: string): SpecialtyGroupRef | null {
  const sep = value?.indexOf(SPECIALTY_OPTION_VALUE_SEPARATOR) ?? -1;
  if (sep <= 0) return null;

  const codeLength = Number(value.substring(0, sep));
  if (!Number.isInteger(codeLength) || codeLength < 0) return null;

  const codeStart = sep + SPECIALTY_OPTION_VALUE_SEPARATOR.length;
  if (codeStart + codeLength > value.length) return null;

  return { code: value.substring(codeStart, codeStart + codeLength), name: value.substring(codeStart + codeLength) };
}

/**
 * Dropdown items for the Specialty Group filter, keyed by {@link specialtyGroupItemKey}.
 *
 * An option whose `value` cannot be decoded is omitted rather than keyed by the raw value: only decodable options
 * appear in {@link specialtyGroupsByItemKey}, so keying it any other way would render a checkbox that silently
 * reverts when clicked.
 */
export function toSpecialtyDropdownItems(options: FilterOption[] | undefined): DropdownItem<string>[] {
  if (!options) return [];
  return options.flatMap((o) => {
    const group = parseSpecialtyOptionValue(o.value);
    return group ? [{ key: specialtyGroupItemKey(group), name: o.displayName }] : [];
  });
}

/** Resolves a dropdown key back to the specialty group decoded from the option's `value`. */
export function specialtyGroupsByItemKey(options: FilterOption[] | undefined): Map<string, SpecialtyGroupRef> {
  const byKey = new Map<string, SpecialtyGroupRef>();
  for (const option of options ?? []) {
    const group = parseSpecialtyOptionValue(option.value);
    if (group) byKey.set(specialtyGroupItemKey(group), group);
  }
  return byKey;
}

/**
 * Human label for a specialty group option: "Name (Code)", or "(Code)" when the name is blank. Used as a
 * fallback when the API's displayName is unavailable, so it must match the server's format.
 */
export function specialtyOptionLabel(code: string, name: string): string {
  // Mirrors the server's DisplayName formula exactly - `$"{name} ({code})".Trim()` - so a blank or
  // whitespace-only name collapses to "(Code)" here too, rather than keeping the padding a truthiness check
  // would let through.
  return `${name ?? ""} (${code})`.trim();
}

export function tagsToNestedItems(
  options: FilterOption[] | undefined,
): NestingParentDropdownItem[] {
  if (!options) return [];
  return options.map((group) => ({
    checkboxStatus: CheckboxStateEnum.Unchecked,
    children: (group.children ?? []).map((tag) => ({
      key: tag.value,
      name: tag.displayName,
    })),
    key: group.value,
    name: group.displayName,
  }));
}

export function serviceAreasToNestedItems(
  options: FilterOption[] | undefined,
): NestingParentDropdownItem[] {
  if (!options) return [];
  return options.map((state) => ({
    checkboxStatus: CheckboxStateEnum.Unchecked,
    children: (state.children ?? []).map((county) => ({
      key: countyKey(state.value, county.value),
      name: county.displayName,
    })),
    key: state.value,
    name: state.displayName,
  }));
}

export function getCustomSelectInputText(selectedCount: number, totalCount: number): string {
  if (selectedCount <= 0 || totalCount <= 0 || selectedCount >= totalCount) {
    return ALL_OPTIONS_TEXT;
  }
  return `${selectedCount} of ${totalCount} item(s) selected`;
}

// !!! Keep the Label Prefix name in sync with the label in savedViewUtils.ts !!!

export function lobChipLabel(displayName: string): string {
  return `LOB: ${displayName}`;
}

export function projectTypeChipLabel(displayName: string): string {
  return `Project Type: ${displayName}`;
}

export function pucChipLabel(displayName: string): string {
  return `Project Utilization: ${displayName}`;
}

export function countyChipLabel(c: CountyRef): string {
  return `Service Area: ${c.county}, ${c.state}`;
}

export function specialtyChipLabel(s: string): string {
  return `Specialty Group: ${s}`;
}

export function templateChipLabel(s: string): string {
  return `Template: ${s}`;
}

export function projectStatusChipLabel(displayName: string): string {
  return `Status: ${displayName}`;
}

export function stateChipLabel(stateAbbrev: string): string {
  return `Service Area: ${stateAbbrev}`;
}

export function getFullySelectedStates(
  counties: readonly CountyRef[],
  filterOptions: FilterOptions | null,
): ReadonlySet<string> {
  if (!filterOptions || counties.length === 0) return new Set();

  const stateCountMap = new Map(
    filterOptions.serviceAreas.map((s) => [s.value, (s.children ?? []).length]),
  );

  const selectedByState = new Map<string, number>();
  for (const c of counties) {
    selectedByState.set(c.state, (selectedByState.get(c.state) ?? 0) + 1);
  }

  const fullStates = new Set<string>();
  for (const [state, count] of selectedByState) {
    const total = stateCountMap.get(state) ?? 0;
    if (total > 0 && count >= total) {
      fullStates.add(state);
    }
  }

  return fullStates;
}

export function buildServiceAreaRequest(
  counties: readonly CountyRef[],
  filterOptions: FilterOptions | null,
): {
  serviceAreaCounty: readonly { readonly county: string; readonly state: string }[];
  serviceAreaState: readonly string[];
} {
  if (counties.length === 0 || !filterOptions) {
    return {
      serviceAreaCounty: counties.map((c) => ({ county: c.county, state: c.state })),
      serviceAreaState: [],
    };
  }

  const fullStates = getFullySelectedStates(counties, filterOptions);
  return {
    serviceAreaCounty: counties
      .filter((c) => !fullStates.has(c.state))
      .map((c) => ({ county: c.county, state: c.state })),
    serviceAreaState: [...fullStates],
  };
}

/**
 * Maps the dashboard's ActiveFilters into the server's ProjectResourcesRequest. Collapses fully-selected states
 * to state-level filtering (via buildServiceAreaRequest), merges them with explicitly-selected states, trims the
 * search term, and omits empty dimensions to keep the payload (and the React Query key) minimal.
 */
export function buildProjectResourcesRequest(
  filters: ActiveFilters,
  filterOptions: FilterOptions | null,
): ProjectResourcesRequest {
  const { serviceAreaCounty, serviceAreaState } = buildServiceAreaRequest(filters.serviceAreaCounties, filterOptions);
  const mergedStates = [...new Set([...filters.serviceAreaStates, ...serviceAreaState])];

  const request: ProjectResourcesRequest = {};
  if (filters.projectTypes.length > 0) request.projectTypes = filters.projectTypes;
  if (filters.linesOfBusinessIds.length > 0) request.linesOfBusinessIds = filters.linesOfBusinessIds;
  const normalizedSearchTerm = filters.searchTerm.trim();
  if (normalizedSearchTerm) request.searchTerm = normalizedSearchTerm;
  if (serviceAreaCounty.length > 0) request.serviceAreaCounties = serviceAreaCounty;
  if (mergedStates.length > 0) request.serviceAreaStates = mergedStates;
  if (filters.specialtyGroups.length > 0) request.specialtyGroups = filters.specialtyGroups.map((s) => ({ code: s.code, name: s.name }));
  if (filters.tagIds.length > 0) request.tagIds = filters.tagIds;
  if (filters.templateNames.length > 0) request.templateNames = filters.templateNames;
  if (filters.projectStatuses.length > 0) request.projectStatuses = filters.projectStatuses;

  // The following are Role Restrict filters.
  if (filters.projectAction) request.projectAction = filters.projectAction;
  if (filters.projectUtilizationCategoryIds.length > 0) request.projectUtilizationCategoryIds = filters.projectUtilizationCategoryIds;
  return request;
}

